import axios from 'axios';
import { setupAxiosInterceptor } from './auth/axiosConfig';
import config from './config';

const billingApi = axios.create({
  baseURL: config.API_BASE_URL + '/cellcog/billing',
});

setupAxiosInterceptor(billingApi);

// ============= Plans =============

/**
 * Get all available subscription plans
 * @returns {Promise} Response with plans array
 */
export const getPlans = () => {
  return billingApi.get('/plans');
};

// ============= Subscription =============

/**
 * Get current subscription status
 * @returns {Promise} Response with subscription, wallet, and plan
 */
export const getSubscription = () => {
  return billingApi.get('/subscription');
};

/**
 * Create checkout session for new subscription
 * @param {string} tier - Subscription tier (BASIC, PRO, PRO_2X, PRO_4X, PRO_8X)
 * @param {string} interval - MONTHLY or YEARLY
 * @param {string|null} returnUrl - Optional custom return URL after payment (e.g., /chat/{id}?billing_success=true)
 * @returns {Promise} Response with checkout_url
 */
export const createCheckout = (tier, interval, returnUrl = null, source = null) => {
  return billingApi.post('/checkout', {
    tier,
    interval,
    return_url: returnUrl,
    // CEL-1737: attribution — where the checkout was INITIATED (e.g. 'welcome').
    // Rides Stripe metadata so completed payments are attributable server-side.
    source,
  });
};

// ============= First-month offer (CEL-3431) =============

/** The account's live offer window: {live, ends_at, seconds_left, dismissed, window_number, plans[]} or {live:false}. */
export const getFirstMonthOffer = () => billingApi.get('/first-month-offer');

/** Start a window from a client moment ('credits_screen' | 'app_open_zero'); the server re-checks everything. */
export const startFirstMonthOffer = (trigger) => billingApi.post('/first-month-offer/start', { trigger });

/** The ×: hides the card in every chat for the rest of the window. */
export const dismissFirstMonthOffer = () => billingApi.post('/first-month-offer/dismiss');

/** The offer first rendered for the owner ('chat' | 'credits_screen'): one server stamp per window. */
export const markFirstMonthOfferSeen = (surface) => billingApi.post('/first-month-offer/seen', { surface });

/**
 * Change subscription plan (tier, interval, and/or seat count)
 * Unified endpoint that handles upgrades (immediate) and downgrades (scheduled)
 * @param {string} newTier - Target subscription tier
 * @param {string} newInterval - Target billing interval (MONTHLY/YEARLY)
 * @param {number|null} newSeatCount - Optional new seat count (for org subscriptions)
 * @param {string|null} returnUrl - Optional custom return URL after payment
 * @returns {Promise} Response with change details
 */
export const changePlan = (newTier, newInterval, newSeatCount = null, returnUrl = null) => {
  const payload = {
    new_tier: newTier,
    new_interval: newInterval,
    return_url: returnUrl,
  };
  if (newSeatCount !== null) {
    payload.new_seat_count = newSeatCount;
  }
  return billingApi.post('/change-plan', payload);
};

/**
 * Cancel a pending scheduled plan change (keeps current plan)
 * @returns {Promise} Response with cancellation status
 */
export const cancelScheduledChange = () => {
  return billingApi.post('/cancel-scheduled-change');
};

/**
 * Cancel subscription at period end
 * @returns {Promise} Response with cancel_at_period_end and access_until
 */
export const cancelSubscription = () => {
  return billingApi.post('/cancel');
};

/**
 * Reactivate subscription set to cancel
 * @returns {Promise} Response with status
 */
export const reactivateSubscription = () => {
  return billingApi.post('/reactivate');
};

/**
 * Get Stripe Customer Portal URL
 * @returns {Promise} Response with portal_url
 */
export const getPortalUrl = () => {
  return billingApi.post('/portal');
};

// ============= Credits =============

/**
 * Get credit balance and transactions
 * @param {number} limit - Number of transactions to return
 * @param {number} offset - Pagination offset
 * @returns {Promise} Response with balance and transactions
 */
export const getCredits = (limit = 50, offset = 0) => {
  return billingApi.get('/credits', { params: { limit, offset } });
};

// ============= Top-Up =============

/**
 * Get top-up pricing information
 * @returns {Promise} Response with credits_per_dollar, preset_amounts, etc.
 */
export const getTopUpInfo = () => {
  return billingApi.get('/top-up/info');
};

/**
 * Create checkout session for credit top-up
 * @param {number} amountDollars - Amount in dollars (10-500)
 * @param {string|null} returnUrl - Optional return URL after payment
 * @returns {Promise} Response with checkout_url, credits_to_receive, expiry_date
 */
export const createTopUpCheckout = (amountDollars, returnUrl = null, source = null) => {
  return billingApi.post('/top-up/checkout', {
    amount_dollars: amountDollars,
    return_url: returnUrl,
    // Attribution (Sep 14 2026, credits page rebuild): where the top-up was INITIATED
    // (e.g. 'credits_wall_chat'). Same contract as createCheckout's source (CEL-1737);
    // stamped into Stripe metadata as checkout_source server-side.
    source,
  });
};

/**
 * Get user's credit buckets with expiry information
 * @returns {Promise} Response with array of bucket info
 */
export const getCreditBuckets = () => {
  return billingApi.get('/credit-buckets');
};

/**
 * Get unified credit history (subscriptions, top-ups, starter, referrals)
 * @returns {Promise} Response with entries array and total count
 */
export const getAutoRecharge = () => {
  return billingApi.get('/auto-recharge');
};

export const updateAutoRecharge = (settings) => {
  // settings: { enabled?, threshold_credits?, target_credits?, monthly_cap_dollars?, remove_monthly_cap? }
  return billingApi.put('/auto-recharge', settings);
};

export const createAutoRechargeCardSetup = (enable = false, returnUrl = null) => {
  return billingApi.post('/auto-recharge/setup-card', null, {
    params: { enable: enable ? 'true' : 'false', ...(returnUrl ? { return_url: returnUrl } : {}) },
  });
};

export const getCreditHistory = () => {
  return billingApi.get('/credit-history');
};

/**
 * Get all plan options with classification and proration preview
 * for the authenticated user's personal subscription.
 * @returns {Promise} Response with current_plan and options map
 */
export const getPlanOptions = () => {
  return billingApi.get('/plan-options');
};
